import React, { useState } from "react";
import { CircularProgress } from "@mui/material";
import { Wordmark } from "@trusty/shared";
import { GRADIENT_BACKGROUND } from "../../styles";
import { BRAND, FONT_BODY, GoogleG, MicrosoftLogo, AppleLogo, CheckBadge, TRUST_LINE } from "./havenBrand";
import { BORDER, Field, Footer, Heading, PrimaryButton, TEXT_MUTED, TextLink, focusField } from "./onboardingControls";
import { MICROSOFT_LOGIN_ENABLED, APPLE_LOGIN_ENABLED } from "../../constants";
import { usePopupSignInReady } from "../../hooks/usePopupSignInReady";
import type { OrgPlan } from "../../lib/signupIntent";

/**
 * Haven account auth screen (onboarding redesign, steps 1–2 + full sign-in/reset).
 *
 * One brand-styled full-page surface covering every signed-out auth state:
 *   • sign-up entry  — Continue with Google / Use another email
 *   • email          — email field → Continue (carries into name + password)
 *   • details        — first/last/password → Create account
 *   • sign-in        — Google / email + password + forgot
 *   • reset password — email → send reset link
 *
 * Wired to the real `useAuth` handlers via props (the same contract the old LoginForm
 * used), so this is the live auth UI — not a mock. Rendered by LoginLayout for the
 * non-post-install case. Light-only by design (the Sage brand surface).
 *
 * COPY for the sign-up entry + email states is EXACT per the redesign spec.
 */

const INVALID_EMAIL = "Enter a valid email address, like you@example.com.";

const PLAN_WELCOME: Record<OrgPlan, { title: string; signUp: string; signIn: string }> = {
  family: {
    title: "Welcome to Haven Family",
    signUp: "Create your account, then name your family and choose how to pay. Your first 30 days are free.",
    signIn: "Sign in to set up Haven Family.",
  },
  business: {
    title: "Welcome to Haven Business",
    signUp: "Create your account, then name your organization. You'll be its owner.",
    signIn: "Sign in to set up Haven Business.",
  },
};

export interface AuthScreenProps {
  error: string | null;
  isSignUp: boolean;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  loading: boolean;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onSubmit: () => void;
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
  onAppleLogin: () => void;
  /** ENG-3625: a provider button is about to be pressed; load the popup sign-in iframe now. */
  onProviderIntent?: () => void;
  onToggleSignUp: () => void;
  onForgotPassword: () => Promise<boolean>;
  onClearError: () => void;
  showForgotPassword: boolean;
  onForgotPasswordToggle: (show: boolean) => void;
  inviteContext: { tenantName: string; inviterEmail: string; inviterName?: string | null; invitedEmail: string; invitedName: string; tenantType?: string } | null;
  /**
   * ENG-3832: this visit holds a demo org claim link. Nothing about it is known yet (the preview needs
   * a signed-in caller, and the link may be dead or refused), so the screen promises nothing about it.
   */
  claimPending?: boolean;
  /** The plan a pricing link named (`?plan=`), so the screen says what signing up leads to. */
  signupPlan?: OrgPlan | null;
  joinResult: { success: boolean; message: string } | null;
  onClearJoinResult: () => void;
}

// ── Primitives ──────────────────────────────────────────────────────────────────
/**
 * ENG-3625: `onIntent` fires when a pointer reaches a provider button, presses it, or focus lands on
 * it, so the popup sign-in iframe starts loading before the click if the warm-up has not finished it
 * yet (lib/firebase.ts, `warmUpPopupResolver`). It is idempotent.
 *
 * `pending` is the brief wait for that iframe on mobile, Safari and iOS (hooks/usePopupSignInReady.ts):
 * the button cannot be pressed, shows a spinner for its logo, and says what it is waiting for.
 */
type ProviderButtonProps = { onClick: () => void; onIntent?: () => void; disabled?: boolean; pending?: boolean };

// ENG-3354: these set `outline: none` with nothing in its place, so a keyboard user could not see
// which one had focus. The ring is `:focus-visible`, so a click still shows none.
const ProviderButton: React.FC<ProviderButtonProps & { provider: string; logo: React.ReactNode; first?: boolean }> = ({
  onClick, onIntent, disabled, pending, provider, logo, first,
}) => {
  const [hover, setHover] = useState(false);
  const off = disabled || pending;
  return (
    <button type="button" onClick={onClick} disabled={off} aria-busy={pending || undefined}
      onPointerEnter={onIntent} onPointerDown={onIntent} onFocus={onIntent}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "14px 18px", fontFamily: FONT_BODY, fontWeight: 600, fontSize: 16, color: BRAND.forest, background: BRAND.white, border: `1.5px solid ${hover ? BRAND.elm : BORDER}`, borderRadius: 100, cursor: off ? "default" : "pointer", boxShadow: hover ? "0 8px 20px rgba(4,43,28,0.14)" : "0 4px 12px rgba(4,43,28,0.08)", transition: "all 0.18s ease", opacity: off ? 0.7 : 1, marginTop: first ? 0 : 12 }}>
      {pending ? <CircularProgress aria-hidden size={18} thickness={5} sx={{ color: BRAND.elm }} /> : logo}
      {pending ? `Loading ${provider} sign-in…` : `Continue with ${provider}`}
    </button>
  );
};

/**
 * The Google, Microsoft and Apple buttons. Mounting them starts the popup sign-in iframe loading, and
 * on mobile, Safari and iOS they wait for it (ENG-3625, hooks/usePopupSignInReady.ts).
 */
const ProviderButtons: React.FC<{
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
  onAppleLogin: () => void;
  onIntent?: () => void;
  disabled: boolean;
}> = ({ onGoogleLogin, onMicrosoftLogin, onAppleLogin, onIntent, disabled }) => {
  const pending = !usePopupSignInReady();
  const shared = { onIntent, disabled, pending };
  return (
    <>
      <ProviderButton {...shared} first provider="Google" logo={<GoogleG />} onClick={onGoogleLogin} />
      {MICROSOFT_LOGIN_ENABLED && <ProviderButton {...shared} provider="Microsoft" logo={<MicrosoftLogo />} onClick={onMicrosoftLogin} />}
      {APPLE_LOGIN_ENABLED && <ProviderButton {...shared} provider="Apple" logo={<AppleLogo color={BRAND.forest} />} onClick={onAppleLogin} />}
    </>
  );
};

const Divider: React.FC = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "18px 0" }}>
    <div style={{ flex: 1, height: 1, background: "rgba(4,43,28,0.1)" }} />
    <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: TEXT_MUTED }}>or</span>
    <div style={{ flex: 1, height: 1, background: "rgba(4,43,28,0.1)" }} />
  </div>
);

const TrustLine: React.FC = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 22 }}>
    <CheckBadge color={BRAND.elm} size={13} />
    <span style={{ fontFamily: FONT_BODY, fontSize: 12, lineHeight: 1.4, color: BRAND.elm, textAlign: "center", whiteSpace: "nowrap" }}>{TRUST_LINE}</span>
  </div>
);

// ── Screen ────────────────────────────────────────────────────────────────────────
type SignUpStep = "choose" | "email" | "details";

export const AuthScreen: React.FC<AuthScreenProps> = (props) => {
  const {
    error, isSignUp, firstName, lastName, email, password, loading,
    onFirstNameChange, onLastNameChange, onEmailChange, onPasswordChange,
    onSubmit, onGoogleLogin, onMicrosoftLogin, onAppleLogin, onProviderIntent, onToggleSignUp, onForgotPassword, onClearError,
    showForgotPassword, onForgotPasswordToggle, inviteContext, claimPending, signupPlan, joinResult, onClearJoinResult,
  } = props;

  const [step, setStep] = useState<SignUpStep>("choose");
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [resetSent, setResetSent] = useState(false);

  // Reset the sign-up sub-step whenever we switch top-level mode (sign-up/in or reset). Done during
  // render rather than in an effect: the effect also ran on mount, after the first paint, so a click on
  // "Use another email" that landed before it had run was undone and the form went back to the choice.
  const mode = `${isSignUp}|${showForgotPassword}`;
  const [modeShown, setModeShown] = useState(mode);
  if (mode !== modeShown) {
    setModeShown(mode);
    setStep("choose");
    setFieldErrors({});
  }

  const validEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const plan = !inviteContext && !claimPending && signupPlan ? PLAN_WELCOME[signupPlan] : null;
  const headline = inviteContext ? `Join ${inviteContext.tenantName}` : claimPending ? "Claim your Haven organization" : plan ? plan.title : "Haven is almost ready";
  const subhead = inviteContext
    ? `${inviteContext.inviterName || inviteContext.inviterEmail} has invited you to join their ${inviteContext.tenantType === "family" ? "family" : "team"} on Haven`
    : claimPending
      ? "Sign in or create an account to continue. Haven checks the link you were sent once you're signed in."
      : plan
        ? plan.signUp
        : "Create your account so Haven can back you up while you browse, open email, and check important sites.";

  // ── Body per state ──
  let body: React.ReactNode;

  if (showForgotPassword) {
    body = (
      <>
        <Heading title="Reset your password" subtitle={resetSent ? undefined : "Enter your email and we'll send you a link to reset your password."} />
        {resetSent && !error ? (
          <div style={successStyle}>Check your inbox — if an account exists for {email}, a reset link is on its way.</div>
        ) : (
          <form onSubmit={async (e) => { e.preventDefault(); if (!validEmail(email)) { setFieldErrors({ email: true }); focusField(e.currentTarget, "email"); return; } setFieldErrors({}); const ok = await onForgotPassword(); setResetSent(ok); }} noValidate>
            <Field label="Email" type="email" name="email" autoComplete="email" value={email} placeholder="you@example.com" autoFocus error={fieldErrors.email ? INVALID_EMAIL : undefined} onChange={(v) => { onEmailChange(v); setFieldErrors({}); }} />
            <PrimaryButton type="submit" disabled={loading}>{loading ? "Sending…" : "Send reset link"}</PrimaryButton>
          </form>
        )}
        <Footer>
          <TextLink onClick={() => { onForgotPasswordToggle(false); setResetSent(false); onClearError(); }}>Back to sign in</TextLink>
        </Footer>
      </>
    );
  } else if (!isSignUp) {
    // Sign-in
    body = (
      <>
        <Heading title="Welcome back" subtitle={plan ? plan.signIn : "Sign in to your Haven account."} />
        <ProviderButtons onGoogleLogin={onGoogleLogin} onMicrosoftLogin={onMicrosoftLogin} onAppleLogin={onAppleLogin} onIntent={onProviderIntent} disabled={loading} />
        <Divider />
        <form onSubmit={(e) => { e.preventDefault(); const errs: Record<string, boolean> = {}; if (!email.trim()) errs.email = true; if (!password.trim()) errs.password = true; if (Object.keys(errs).length) { setFieldErrors(errs); focusField(e.currentTarget, Object.keys(errs)[0]); return; } onSubmit(); }} noValidate>
          <Field label="Email" type="email" name="email" autoComplete="email" value={email} placeholder="you@example.com" error={fieldErrors.email ? "Enter your email address." : undefined} onChange={(v) => { onEmailChange(v); setFieldErrors((p) => ({ ...p, email: false })); }} />
          <Field label="Password" type="password" name="password" autoComplete="current-password" value={password} error={fieldErrors.password ? "Enter your password." : undefined} onChange={(v) => { onPasswordChange(v); setFieldErrors((p) => ({ ...p, password: false })); }} />
          <div style={{ textAlign: "right", marginBottom: 18, marginTop: -4 }}>
            <TextLink onClick={() => { onForgotPasswordToggle(true); onClearError(); }}>Forgot password?</TextLink>
          </div>
          <PrimaryButton type="submit" disabled={loading}>{loading ? "Signing in…" : "Sign in"}</PrimaryButton>
        </form>
        <Footer>New to Haven? <TextLink onClick={onToggleSignUp}>Create account</TextLink></Footer>
      </>
    );
  } else if (step === "details") {
    // Sign-up: name + password
    body = (
      <>
        <Heading title="Create your account" subtitle={`You're signing up as ${email}`} />
        <form onSubmit={(e) => { e.preventDefault(); const errs: Record<string, boolean> = {}; if (!firstName.trim()) errs.firstName = true; if (!lastName.trim()) errs.lastName = true; if (!password.trim()) errs.password = true; if (Object.keys(errs).length) { setFieldErrors(errs); focusField(e.currentTarget, Object.keys(errs)[0]); return; } onSubmit(); }} noValidate>
          <Field label="First name" type="text" name="firstName" autoComplete="given-name" value={firstName} autoFocus error={fieldErrors.firstName ? "Enter your first name." : undefined} onChange={(v) => { onFirstNameChange(v); setFieldErrors((p) => ({ ...p, firstName: false })); }} />
          <Field label="Last name" type="text" name="lastName" autoComplete="family-name" value={lastName} error={fieldErrors.lastName ? "Enter your last name." : undefined} onChange={(v) => { onLastNameChange(v); setFieldErrors((p) => ({ ...p, lastName: false })); }} />
          <Field label="Password" type="password" name="password" autoComplete="new-password" value={password} error={fieldErrors.password ? "Create a password." : undefined} onChange={(v) => { onPasswordChange(v); setFieldErrors((p) => ({ ...p, password: false })); }} />
          <PrimaryButton type="submit" disabled={loading}>{loading ? "Creating account…" : "Create account"}</PrimaryButton>
        </form>
        <Footer><TextLink onClick={() => { setStep("email"); onClearError(); }}>← Back</TextLink></Footer>
      </>
    );
  } else if (step === "email") {
    // Sign-up: email entry
    body = (
      <>
        <Heading title={headline} subtitle={subhead} />
        <form onSubmit={(e) => { e.preventDefault(); if (!validEmail(email)) { setFieldErrors({ email: true }); focusField(e.currentTarget, "email"); return; } setFieldErrors({}); onClearError(); setStep("details"); }} noValidate>
          <Field label="Email" type="email" name="email" autoComplete="email" value={email} placeholder="you@example.com" autoFocus error={fieldErrors.email ? INVALID_EMAIL : undefined} onChange={(v) => { onEmailChange(v); setFieldErrors({}); }} />
          <PrimaryButton type="submit" disabled={loading}>Continue</PrimaryButton>
        </form>
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <TextLink onClick={() => { setStep("choose"); setFieldErrors({}); }}>Use Google{MICROSOFT_LOGIN_ENABLED ? " or Microsoft" : ""} instead</TextLink>
        </div>
        <TrustLine />
      </>
    );
  } else {
    // Sign-up: entry (choose)
    body = (
      <>
        <Heading title={headline} subtitle={subhead} />
        <ProviderButtons onGoogleLogin={onGoogleLogin} onMicrosoftLogin={onMicrosoftLogin} onAppleLogin={onAppleLogin} onIntent={onProviderIntent} disabled={loading} />
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <TextLink onClick={() => { setStep("email"); setFieldErrors({}); }}>Use another email</TextLink>
        </div>
        <TrustLine />
        <Footer>Already have an account? <TextLink onClick={onToggleSignUp}>Sign in</TextLink></Footer>
      </>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: GRADIENT_BACKGROUND, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, boxSizing: "border-box", fontFamily: FONT_BODY }}>
      {/* Top-right homepage link */}
      <a href="https://starthaven.com" style={{ position: "absolute", top: 20, right: 24, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 500, color: BRAND.elm, textDecoration: "none" }}>Homepage</a>

      {/* Join result banner */}
      {joinResult && (
        // Keeps role="alert" — that is what makes the message announce at all, and overriding it
        // with "button" would trade the announcement for a dismiss affordance. Operability is added
        // without touching the role, the same call made for table rows in lib/a11y.
        <div
          role="alert"
          tabIndex={0}
          onClick={onClearJoinResult}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " " && e.key !== "Escape") return;
            e.preventDefault();
            onClearJoinResult();
          }}
          style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 1400, maxWidth: 500, fontFamily: FONT_BODY, fontSize: 14, padding: "12px 18px", borderRadius: 12, cursor: "pointer", color: joinResult.success ? "#1A7A4B" : "#8a1c1c", background: joinResult.success ? "#DCF2E1" : "#fdeaea", border: `1px solid ${joinResult.success ? "#A3E7AE" : "#f5c2c2"}` }}>
          {joinResult.message}
        </div>
      )}

      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <div style={{ width: 170 }}><Wordmark /></div>
        </div>

        <div style={{ background: BRAND.white, borderRadius: 20, padding: "36px 32px", boxShadow: "0 18px 48px rgba(4,43,28,0.12), 0 4px 12px rgba(4,43,28,0.06)" }}>
          {error && (
            <div role="alert" style={{ fontFamily: FONT_BODY, fontSize: 14, color: "#8a1c1c", background: "#fdeaea", border: "1px solid #f5c2c2", borderRadius: 12, padding: "10px 14px", marginBottom: 20, textAlign: "center" }}>{error}</div>
          )}
          {body}
        </div>
      </div>
    </div>
  );
};

// ── Local layout helpers ──
const successStyle: React.CSSProperties = { fontFamily: FONT_BODY, fontSize: 14, color: "#1A7A4B", background: "#DCF2E1", border: "1px solid #A3E7AE", borderRadius: 12, padding: "12px 16px", textAlign: "center" };

export default AuthScreen;
